import React, {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {useAtomValue, useSetAtom} from "jotai";
import {Avatar, Box, Stack, Typography} from "@mui/material";
import {Dialog} from "../base/Dialog";
import {
    authenticatedUserNotificationsStatsAtom,
    authenticatedUserNotificationsStatsLoadable,
    authenticatedUserPreferencesLoadable,
} from "../../store/authenticatedUserStore";
import {InvitationNotificationCard} from "../notifications/InvitationNotificationCard";
import {useAuthUserNotificationItems} from "../../hooks/authUserNotificationItemsHooks";
import {OrderNotificationCard} from "../notifications/OrderNotificationCard";
import {NotificationsIcon} from "../icons/NotificationsIcon";
import {useNotifications, useUnSeenNotifications} from "../../hooks/notificationsHook";
import {NotificationCard} from "../notifications/NotificationCard";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import "./styles/NotificationDialog.css";
import {notificationSeen} from "../../services/notificationsService";
import {useQueryClient} from "@tanstack/react-query";
import {useLoadableValueOrDefault} from "../../hooks/jotaiHooks";
import {UserPreferences} from "../../types/domain/user";
import {DEFAULT_USER_PREFERENCES} from "../../utils/userUtils";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const NotificationsDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();
    const notificationItemsState = useAuthUserNotificationItems();
    const {refresh: refreshItems, notificationItems} = notificationItemsState;
    const refreshNotificationsStats = useSetAtom(authenticatedUserNotificationsStatsAtom);
    const notificationsStatsState = useAtomValue(authenticatedUserNotificationsStatsLoadable);
    const notifications = useNotifications();
    const unSeenNotifications = useUnSeenNotifications();
    const queryClient = useQueryClient();
    const userPreferences = useLoadableValueOrDefault<UserPreferences>(
        authenticatedUserPreferencesLoadable,
        DEFAULT_USER_PREFERENCES,
    );

    const notificationsNumber = useMemo(() => {
        return notificationsStatsState.state === "hasData"
            ? notificationsStatsState.data.pendingInvitations +
                  notificationsStatsState.data.pendingOrders +
                  (unSeenNotifications?.length ? unSeenNotifications.length : 0)
            : 0;
    }, [unSeenNotifications, notificationsStatsState]);

    const onRefresh = useCallback(() => {
        refreshItems();
        refreshNotificationsStats();
    }, [refreshItems, refreshNotificationsStats]);

    return (
        <Dialog
            width="50rem"
            title={renderHeader(notificationsNumber, t)}
            titleIcon={
                <NotificationsIcon
                    sx={{
                        stroke: "#404DB8",
                        width: "1.125rem",
                        height: "1.125rem",
                    }}
                />
            }
            cancelTitle={t("dialogs.actions.close")}
            open={open}
            onClose={() => onClose(false)}
        >
            <Stack spacing={2}>
                {notificationItems.length === 0 && notifications.length === 0 && (
                    <Typography>{t("dialogs.notifications.no_notifications")}</Typography>
                )}

                {notifications
                    ?.sort((a, b) => (dayjs(a.createdAt).valueOf() - dayjs(b.startTime).valueOf() ? -1 : 1))
                    .map((notification) => {
                        return (
                            <NotificationCard
                                key={notification.id}
                                header={
                                    notification?.metaData?.content?.[userPreferences.language ?? "en"]?.title ?? ""
                                }
                                date={dayjs(notification.createdAt).format("DD-MM-YYYY")}
                                isLoading={false}
                                isPending={typeof notification.seen === "boolean" ? !notification.seen : false}
                                error={undefined}
                                collapseOnEnterCallback={() => {
                                    if (notification?.id) {
                                        void notificationSeen(notification.id).then(() => {
                                            void queryClient.refetchQueries({queryKey: ["notifications"]});
                                        });
                                    }
                                }}
                            >
                                <div
                                    className="notification-dialog-notification-content"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            notification.metaData?.content?.[userPreferences.language ?? "en"]
                                                ?.content ?? "",
                                        ),
                                    }}
                                />
                            </NotificationCard>
                        );
                    })}

                {notificationItems.length > 0 &&
                    notificationItems.map((item) =>
                        item.notificationType === "Invitation" ? (
                            <InvitationNotificationCard
                                key={item.invitation.id}
                                invitation={item.invitation}
                                onRefresh={onRefresh}
                            />
                        ) : (
                            <OrderNotificationCard key={item.order.id} order={item.order} />
                        ),
                    )}
            </Stack>
        </Dialog>
    );
};

const renderHeader = (pendingNotifications: number, t: TFunction<"translation", undefined>) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "row",
            gap: "0.75rem",
            alignItems: "center",
        }}
    >
        <div>{t("dialogs.notifications.title")}</div>
        {pendingNotifications > 0 && (
            <Avatar
                variant="rounded"
                sx={{
                    bgcolor: "#CBD3F0",
                    color: "#303A8C",
                    width: 24,
                    height: 24,
                    fontSize: 14,
                }}
            >
                {pendingNotifications}
            </Avatar>
        )}
    </Box>
);
